/**
 * Os campos que uma coluna de planilha pode alimentar, e as quatro
 * dimensões de classificação que a pessoa pode mandar tomar da planilha.
 *
 * Espelho de `core.spreadsheet.template.CANONICAL_FIELDS` e de
 * `core.vocabulary.IMPORTABLE_DIMENSIONS`, com paridade travada por
 * `tests/test_canonical_fields_parity.py` (lê este arquivo). A tupla é
 * `as const` e os mapas de rótulo e de grupo são tipados sobre ela DE
 * PROPÓSITO: campo a mais ou a menos é erro de compilação — que é como
 * `FIELD_LABELS` (um `Record<string, string>` escrito à mão dentro da
 * tela de templates) deixaria os quatro campos de classificação
 * aparecerem CRUS no seletor.
 *
 * Mapear uma coluna de classificação NÃO liga nada: quem decide usar a
 * classificação da planilha é a pessoa, no envio, uma dimensão por vez
 * ("Configurações avançadas"). Padrão desligado.
 */

export const CANONICAL_FIELDS = [
  "title",
  "content",
  "published_at",
  "link",
  "original_link",
  "channel",
  "native_id",
  "author_id",
  "author_name",
  "author_profile_url",
  "likes",
  "reactions",
  "comments",
  "shares",
  "views",
  "follower_count",
  "duration_seconds",
  "ad_value",
  "reach",
  "vehicle_state",
  "vehicle_kind",
  "polarity",
  "approach",
  "themes",
  "key_messages"
] as const;

export type CanonicalField = (typeof CANONICAL_FIELDS)[number];

export const CANONICAL_FIELD_LABELS: Record<CanonicalField, string> = {
  title: "Título",
  content: "Texto",
  published_at: "Data de publicação",
  link: "Link (clipadora)",
  original_link: "Link original",
  channel: "Canal / veículo",
  native_id: "Código na clipadora",
  author_id: "Autor (id)",
  author_name: "Autor (nome)",
  author_profile_url: "Perfil do autor (URL)",
  likes: "Curtidas",
  reactions: "Reações",
  comments: "Comentários",
  shares: "Compartilhamentos",
  views: "Visualizações",
  follower_count: "Seguidores",
  duration_seconds: "Duração (s)",
  ad_value: "Valoração",
  reach: "Alcance (imprensa)",
  vehicle_state: "UF do veículo",
  vehicle_kind: "Tipo do veículo",
  polarity: "Polaridade",
  approach: "Abordagem",
  themes: "Subtemas",
  key_messages: "Mensagens-chave"
};

export type CanonicalFieldGroup = "post" | "engagement" | "classification";

export const CANONICAL_FIELD_GROUP: Record<CanonicalField, CanonicalFieldGroup> = {
  title: "post",
  content: "post",
  published_at: "post",
  link: "post",
  original_link: "post",
  channel: "post",
  native_id: "post",
  author_id: "post",
  author_name: "post",
  author_profile_url: "post",
  duration_seconds: "post",
  ad_value: "post",
  reach: "post",
  // Só desempatam veículos com o mesmo nome (2026-11-03); nunca mudam o
  // cadastro do veículo.
  vehicle_state: "post",
  vehicle_kind: "post",
  likes: "engagement",
  reactions: "engagement",
  comments: "engagement",
  shares: "engagement",
  views: "engagement",
  follower_count: "engagement",
  polarity: "classification",
  approach: "classification",
  themes: "classification",
  key_messages: "classification"
};

/** O nome do grupo no seletor de campo do template. */
export const CANONICAL_GROUP_LABELS: Record<CanonicalFieldGroup, string> = {
  post: "Publicação",
  engagement: "Engajamento",
  classification: "Classificação pronta (opcional)"
};

/** As dimensões de classificação que o envio pode tomar da planilha —
 *  nomes de CAMPO CANÔNICO, a mesma palavra no template, no envio e na
 *  fila de correção. */
export const IMPORTABLE_DIMENSIONS = [
  "polarity",
  "approach",
  "themes",
  "key_messages"
] as const;

export type ImportableDimension = (typeof IMPORTABLE_DIMENSIONS)[number];

export function isCanonicalField(value: string): value is CanonicalField {
  return (CANONICAL_FIELDS as readonly string[]).includes(value);
}

export function isImportableDimension(value: string): value is ImportableDimension {
  return (IMPORTABLE_DIMENSIONS as readonly string[]).includes(value);
}

/** Campo desconhecido não some: cai no identificador cru. É o caso de o
 *  servidor ganhar um campo antes do front. */
export function canonicalFieldLabel(field: string): string {
  return isCanonicalField(field) ? CANONICAL_FIELD_LABELS[field] : field;
}

export type FieldGroupOption = {
  group: CanonicalFieldGroup | "other";
  label: string;
  fields: string[];
};

const GROUP_ORDER: CanonicalFieldGroup[] = ["post", "engagement", "classification"];

/** Os campos que o SERVIDOR ofereceu, agrupados pro `<optgroup>`. Mantém
 *  a ordem do servidor dentro de cada grupo, e o campo que o front não
 *  conhece vai pra um grupo "Outros" no fim, em vez de desaparecer do
 *  seletor (uma coluna já mapeada pra ele ficaria sem opção marcada). */
export function groupCanonicalFields(fields: readonly string[]): FieldGroupOption[] {
  const groups: FieldGroupOption[] = GROUP_ORDER.map(group => ({
    group,
    label: CANONICAL_GROUP_LABELS[group],
    fields: fields.filter(f => isCanonicalField(f) && CANONICAL_FIELD_GROUP[f] === group)
  }));
  const unknown = fields.filter(f => !isCanonicalField(f));
  if (unknown.length) groups.push({ group: "other", label: "Outros", fields: unknown });
  return groups.filter(g => g.fields.length > 0);
}
